import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Input } from '../ui'
import { PasswordInput } from '../ui/password-input'
import { Alert, AlertDescription } from '../ui/alert'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Label } from '../ui/label'

interface LoginFormProps {
  setEmail: (email: string) => void
  setPassword: (password: string) => void
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  isLoading?: boolean
  formError?: string
}

export function LoginForm({ setEmail, setPassword, handleSubmit, isLoading, formError }: LoginFormProps) {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        <div className="flex min-h-[500px]">
          {/* Left Panel - Marketing Content */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}
            />
            {/* Header */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              <div className="flex items-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8" />
              </div>
            </div>
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md">
                <h1 className="text-4xl font-bold mb-4 text-white">Capturing Moments, Creating Memories</h1>
                <p className="text-lg opacity-90 mb-8 text-white">
                  Access your FanFair Partner Portal to manage events, view analytics, and grow your business.
                </p>
                {/* Slide indicators */}
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Panel - Login Form */}
          <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-medium text-white">Sign in to your account</h1>
                <p className="text-slate-400 font-light">Welcome back! Please enter your details.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                {formError && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription className="text-white">{formError}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-light text-white">
                      Email
                    </Label>
                    <Input
                      className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20"
                      placeholder="Enter your email"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      data-testid="email-input"
                      type="email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-light text-white">
                      Password
                    </Label>
                    <div className="relative">
                      <PasswordInput
                        className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20 pr-10"
                        placeholder="Enter your password"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        data-testid="password-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Link to="/auth/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  disabled={isLoading}
                  data-testid="login-button"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
